import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useAnnouncements } from "../lib/announcements";
import { membershipLabel } from "../lib/memberships";
import { Avatar } from "./Avatar";

const TYPE_ICON = { info: "ℹ", warning: "⚠", maintenance: "🔧" };

// The panel renders no dismiss control of its own — it is closed by the same
// caret button that opened it, so the `onClose` callback Topbar used to pass was
// never wired to anything (#2177). Adding a dismiss affordance means taking the
// prop back AND rendering the control.
function NotificationPanel({ undismissed, dismiss }) {
  if (undismissed.length === 0) {
    return (
      <div className="notif-panel">
        <div className="notif-empty">No new notifications</div>
      </div>
    );
  }
  return (
    <div className="notif-panel">
      {undismissed.map((a) => (
        <div key={a.id} className={`notif-item notif-${a.type}`}>
          <span className="notif-icon">{TYPE_ICON[a.type] ?? "•"}</span>
          <div className="notif-body">
            <div className="notif-title">{a.title}</div>
            {a.body && <div className="notif-text">{a.body}</div>}
          </div>
          <button
            className="notif-dismiss"
            onClick={() => { dismiss(a.id); }}
            aria-label="Dismiss"
          >✕</button>
        </div>
      ))}
    </div>
  );
}

// ─── Workspace Switcher ────────────────────────────────────────────────────
// Lives on the RIGHT side of the topbar. Consolidates archer switching,
// theme toggle, command palette, notifications, and sign-out into one
// dropdown to save horizontal space on portrait phones.
function WsSwitcher({ onLogout, onToggleTheme, onOpenPalette }) {
  const { archers, activeArcher, selectArcher } = useAuth();
  const { undismissed, dismiss } = useAnnouncements();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const name = activeArcher?.name ?? "Loading…";

  function choose(id) {
    selectArcher(id);
    setOpen(false);
  }

  return (
    <div className="ws-switcher" ref={ref} onClick={() => setOpen((o) => !o)}>
      <Avatar archer={activeArcher} />
      <div className="ws-name">{name}</div>
      <svg
        className={"ws-caret" + (open ? " open" : "")}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>

      {open && (
        <div className="ws-dropdown" onClick={(e) => e.stopPropagation()}>
          {archers.map(({ archer, role, is_self }) => {
            const isActive = activeArcher?.id === archer.id;
            return (
              <button
                key={archer.id}
                type="button"
                className={"ws-dropdown-item" + (isActive ? " active" : "")}
                onClick={() => choose(archer.id)}
              >
                <Avatar archer={archer} className="ws-avatar sm" />
                <div className="ws-dropdown-body">
                  <div className="ws-dropdown-name">{archer.name}</div>
                  <div className="ws-dropdown-role">{membershipLabel({ role, is_self })}</div>
                </div>
                {isActive && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ws-check">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}

          <div className="ws-dropdown-divider" />

          <button
            type="button"
            className={"ws-dropdown-item" + (notifOpen ? " active" : "")}
            onClick={(e) => { e.stopPropagation(); setNotifOpen((o) => !o); }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <div className="ws-dropdown-body">
              <div className="ws-dropdown-name">
                Notifications
                {undismissed.length > 0 && (
                  <span className="ws-notif-badge">{undismissed.length}</span>
                )}
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ws-notif-caret">
              <path d={notifOpen ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
            </svg>
          </button>
          {notifOpen && (
            <NotificationPanel undismissed={undismissed} dismiss={dismiss} />
          )}

          <button type="button" className="ws-dropdown-item" onClick={() => { setOpen(false); onToggleTheme?.(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon icon-moon">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon icon-sun">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
            </svg>
            <div className="ws-dropdown-body">
              <div className="ws-dropdown-name">
                <span className="theme-label-to-dark">Night mode</span>
                <span className="theme-label-to-light">Day mode</span>
              </div>
            </div>
          </button>

          <button type="button" className="ws-dropdown-item" onClick={() => { setOpen(false); onOpenPalette?.(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon">
              <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
            </svg>
            <div className="ws-dropdown-body">
              <div className="ws-dropdown-name">Command palette <span className="ws-kbd">⌘K</span></div>
            </div>
          </button>

          <button type="button" className="ws-dropdown-item" onClick={() => { setOpen(false); navigate('/help'); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
            <div className="ws-dropdown-body">
              <div className="ws-dropdown-name">Help</div>
            </div>
          </button>

          <button type="button" className="ws-dropdown-item" onClick={() => { setOpen(false); onLogout?.(); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="ws-dropdown-icon">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="m16 17 5-5-5-5M21 12H9" />
            </svg>
            <div className="ws-dropdown-body">
              <div className="ws-dropdown-name">Sign out</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Topbar ────────────────────────────────────────────────────────────────
export function Topbar({
  crumbs = null,
  onToggleTheme,
  onOpenPalette,
  onOpenNav,
}) {
  const { logout } = useAuth();
  return (
    <header className="topbar">
      <div className="topbar-l">
        {/* Narrow screens hide the sidebar — this opens it as a drawer. */}
        <button
          className="topbar-burger icon-btn"
          aria-label="Open navigation"
          onClick={onOpenNav}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {crumbs ? (
          <div className="crumbs">
            {crumbs.map((c, i) => (
              <Fragment key={i}>
                {i > 0 && <span className="sep">/</span>}
                <span className={i === crumbs.length - 1 ? "here" : ""}>{c}</span>
              </Fragment>
            ))}
          </div>
        ) : (
          <div className="search search--button" onClick={onOpenPalette}>
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              placeholder="Search tournaments, archers, clubs…"
              readOnly
            />
            <span className="search-kbd">⌘K</span>
          </div>
        )}
      </div>

      <div className="topbar-r">
        <WsSwitcher
          onLogout={logout}
          onToggleTheme={onToggleTheme}
          onOpenPalette={onOpenPalette}
        />
      </div>
    </header>
  );
}
